import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Loader2, Trash2, Plus } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import PromoEditor from "./PromoEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const iso = (d) => d.toISOString().slice(0, 10);

const CHANNELS = ["QR Code", "Facebook Groups", "Facebook Marketplace", "Facebook Ads", "TikTok",
  "Instagram", "Google", "SMS Keyword", "Referral", "Direct"];

function rangeFor(preset) {
  const today = new Date();
  if (preset === "today") return [iso(today), iso(today)];
  if (preset === "week") {
    const s = new Date(today); s.setDate(today.getDate() - today.getDay());
    return [iso(s), iso(today)];
  }
  const s = new Date(today.getFullYear(), today.getMonth(), 1);
  return [iso(s), iso(today)];
}

export default function WeeklySpecialHub({ token }) {
  return (
    <div className="space-y-5" data-testid="weekly-special-hub">
      <PromoEditor token={token} />
      <PerformanceReport token={token} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <FeaturedVehicleCard token={token} />
        <TrackingCard token={token} />
      </div>
    </div>
  );
}

function PerformanceReport({ token }) {
  const [preset, setPreset] = useState("month");
  const [custom, setCustom] = useState({ start: rangeFor("month")[0], end: iso(new Date()) });
  const [d, setD] = useState(null);

  const [start, end] = preset === "custom" ? [custom.start, custom.end] : rangeFor(preset);

  const load = useCallback(() => {
    if (!start || !end) return;
    axios.get(`${API}/weekly-special/report?start=${start}&end=${end}`, auth(token))
      .then((r) => setD(r.data)).catch(() => toast.error("Could not load the report."));
  }, [token, start, end]);
  useEffect(() => { load(); }, [load]);

  const presetBtn = (id, label) => (
    <button key={id} data-testid={`ws-report-range-${id}`} onClick={() => setPreset(id)}
      className={`px-3.5 py-1.5 rounded-md text-xs font-bold transition-colors ${preset === id ? "bg-[#F2B705] text-black" : "bg-white/[0.04] border border-white/10 text-white/60"}`}>
      {label}
    </button>
  );

  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="weekly-special-report">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h4 className="text-white font-semibold">Weekly Special Performance</h4>
          <p className="text-white/40 text-xs">Full funnel: visitors → leads → bookings → revenue, by marketing channel.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {presetBtn("today", "Today")}{presetBtn("week", "This Week")}{presetBtn("month", "This Month")}{presetBtn("custom", "Custom")}
          {preset === "custom" && (
            <span className="flex items-center gap-1.5">
              <input data-testid="ws-report-start" type="date" value={custom.start} onChange={(e) => setCustom((p) => ({ ...p, start: e.target.value }))}
                className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-white text-xs" />
              <span className="text-white/40 text-xs">to</span>
              <input data-testid="ws-report-end" type="date" value={custom.end} onChange={(e) => setCustom((p) => ({ ...p, end: e.target.value }))}
                className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-white text-xs" />
            </span>
          )}
        </div>
      </div>

      {!d ? (
        <div className="flex items-center gap-2 text-white/50 py-10 justify-center"><Loader2 className="animate-spin" size={17} /> Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <Kpi label="Page Visitors" value={d.visitors.unique} sub={`${d.visitors.views} views`} testid="ws-kpi-visitors" />
            <Kpi label="Leads" value={d.leads_total} testid="ws-kpi-leads" />
            <Kpi label="Bookings" value={d.bookings_total} testid="ws-kpi-bookings" />
            <Kpi label="Visitor → Lead" value={`${d.conv_visitor_to_lead}%`} testid="ws-kpi-conv-vl" />
            <Kpi label="Lead → Booking" value={`${d.conv_lead_to_booking}%`} testid="ws-kpi-conv-lb" />
            <Kpi label="Revenue" value={fmt$(d.revenue)} sub={`${fmt$(d.collected)} collected`} gold testid="ws-kpi-revenue" />
            <Kpi label="Avg / Booking" value={fmt$(d.avg_revenue_per_booking)} testid="ws-kpi-avg" />
            <Kpi label="ROAS" value={d.roas != null ? `${d.roas}×` : "—"} sub={d.total_spend > 0 ? `${fmt$(d.total_spend)} spend` : "no ad spend logged"} testid="ws-kpi-roas" />
          </div>

          {d.daily.length > 0 && (
            <div className="mt-5 h-52">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={d.daily}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis allowDecimals={false} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} width={26} />
                  <Tooltip contentStyle={{ background: "#0C0C0C", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="leads" name="Leads" fill="#F2B705" radius={[3, 3, 0, 0]} maxBarSize={26} />
                  <Line dataKey="bookings" name="Bookings" stroke="#34d399" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm" data-testid="ws-channel-table">
              <thead>
                <tr className="text-white/40 text-[11px] uppercase tracking-wider text-left">
                  <th className="py-2 pr-3">Channel</th><th className="py-2 pr-3">Leads</th><th className="py-2 pr-3">Bookings</th>
                  <th className="py-2 pr-3">Conv.</th><th className="py-2 pr-3">Revenue</th><th className="py-2 pr-3">Ad Spend</th><th className="py-2">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {d.channels.filter((c) => c.leads || c.bookings || c.spend).map((c) => (
                  <tr key={c.channel} className="border-t border-white/[0.06] text-white/80">
                    <td className="py-2 pr-3 font-semibold">{c.channel}</td>
                    <td className="py-2 pr-3">{c.leads}</td>
                    <td className="py-2 pr-3">{c.bookings}</td>
                    <td className="py-2 pr-3">{c.leads ? `${Math.round((100 * c.bookings) / c.leads)}%` : "—"}</td>
                    <td className="py-2 pr-3 text-[#F2B705] font-semibold">{fmt$(c.revenue)}</td>
                    <td className="py-2 pr-3">{c.spend ? fmt$(c.spend) : "—"}</td>
                    <td className="py-2 font-bold">{c.roas != null ? `${c.roas}×` : "—"}</td>
                  </tr>
                ))}
                {d.channels.every((c) => !c.leads && !c.bookings && !c.spend) && (
                  <tr><td colSpan={7} className="py-6 text-center text-white/35 text-xs">No Weekly Special activity in this range yet. Share the /special link to start filling this in.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <AdSpendEditor token={token} entries={d.spend_entries} onChanged={load} />
        </>
      )}
    </div>
  );
}

const Kpi = ({ label, value, sub, gold, testid }) => (
  <div data-testid={testid} className={`rounded-lg p-3 border ${gold ? "bg-[#F2B705]/[0.08] border-[#F2B705]/30" : "bg-white/[0.03] border-white/10"}`}>
    <p className="text-white/40 text-[10px] uppercase tracking-wider">{label}</p>
    <p className={`font-display font-extrabold text-xl mt-0.5 ${gold ? "text-[#F2B705]" : "text-white"}`}>{value}</p>
    {sub && <p className="text-white/35 text-[10px] mt-0.5">{sub}</p>}
  </div>
);

function AdSpendEditor({ token, entries, onChanged }) {
  const [f, setF] = useState({ channel: "Facebook Ads", amount: "", date: iso(new Date()), note: "" });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!f.amount || Number(f.amount) <= 0) { toast.error("Enter a spend amount."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/weekly-special/ad-spend`, { ...f, amount: Number(f.amount) }, auth(token));
      toast.success("Ad spend logged — ROAS updated.");
      setF((p) => ({ ...p, amount: "", note: "" }));
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/weekly-special/ad-spend/${id}`, auth(token));
      onChanged();
    } catch { toast.error("Could not delete."); }
  };

  return (
    <div className="mt-5 border-t border-white/[0.08] pt-4" data-testid="ws-adspend-editor">
      <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">Ad Spend Log (powers ROAS)</p>
      <div className="flex flex-wrap items-end gap-2">
        <select data-testid="ws-adspend-channel" value={f.channel} onChange={(e) => setF((p) => ({ ...p, channel: e.target.value }))}
          className="bg-[#0C0C0C] border border-white/15 rounded px-2 py-1.5 text-white text-xs">
          {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input data-testid="ws-adspend-amount" type="number" min="0" step="0.01" placeholder="$ amount" value={f.amount}
          onChange={(e) => setF((p) => ({ ...p, amount: e.target.value }))}
          className="w-24 bg-white/[0.04] border border-white/15 rounded px-2 py-1.5 text-white text-xs" />
        <input data-testid="ws-adspend-date" type="date" value={f.date} onChange={(e) => setF((p) => ({ ...p, date: e.target.value }))}
          className="bg-white/[0.04] border border-white/15 rounded px-2 py-1.5 text-white text-xs" />
        <input data-testid="ws-adspend-note" placeholder="note (optional)" value={f.note} onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))}
          className="flex-1 min-w-32 bg-white/[0.04] border border-white/15 rounded px-2 py-1.5 text-white text-xs" />
        <button data-testid="ws-adspend-add" onClick={add} disabled={busy}
          className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#F2B705] text-black font-bold text-xs disabled:opacity-50">
          <Plus size={13} /> Log Spend
        </button>
      </div>
      {entries.length > 0 && (
        <div className="mt-3 space-y-1">
          {entries.map((s) => (
            <div key={s.id} className="flex items-center gap-3 text-xs text-white/60 bg-white/[0.02] rounded px-3 py-1.5">
              <span className="font-semibold text-white/80 w-36">{s.channel}</span>
              <span className="text-[#F2B705] font-bold">{fmt$(s.amount)}</span>
              <span>{s.date}</span>
              <span className="flex-1 truncate">{s.note}</span>
              <button data-testid={`ws-adspend-del-${s.id}`} onClick={() => remove(s.id)} className="text-white/30 hover:text-red-400"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeaturedVehicleCard({ token }) {
  const [vehicles, setVehicles] = useState([]);
  const [featuredId, setFeaturedId] = useState("");
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/vehicles`).then((r) => setVehicles(r.data || [])).catch(() => {});
    axios.get(`${API}/weekly-special`).then((r) => {
      setCurrent(r.data.featured);
      setFeaturedId(r.data.featured?.id || "");
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/weekly-special/featured`, { vehicle_id: featuredId || null }, auth(token));
      toast.success(featuredId ? "Featured vehicle updated — the landing page shows it immediately." : "Featured vehicle cleared — the landing page shows the general fleet offer.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="weekly-special-featured-card">
      <h4 className="text-white font-semibold text-sm mb-1">Featured Vehicle</h4>
      <p className="text-white/40 text-xs mb-4">
        Pick which vehicle stars on <span className="text-[#F2B705] font-mono">/weekly-special</span>. Swap it anytime — no code changes.
        When nothing is selected, the page shows a general fleet image with &ldquo;Vehicles starting at $375/week.&rdquo;
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <label className="text-xs text-white/50 uppercase tracking-wider block md:col-span-2">Featured Vehicle
          <select data-testid="weekly-special-vehicle-select" value={featuredId} onChange={(e) => setFeaturedId(e.target.value)}
            className="mt-1 w-full bg-[#0C0C0C] border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm">
            <option value="">— None (general fleet offer) —</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.year} {v.make} {v.model}{v.status && v.status !== "available" ? ` — ${v.status.toUpperCase()}` : ""}</option>
            ))}
          </select>
        </label>
        <button data-testid="weekly-special-vehicle-save" onClick={save} disabled={saving}
          className="flex items-center justify-center gap-1.5 px-5 py-2 rounded-md bg-[#F2B705] text-black font-bold text-sm disabled:opacity-50">
          <Save size={14} /> {saving ? "Saving…" : "Set Featured"}
        </button>
      </div>
      {current && (
        <p className="text-white/40 text-xs mt-3" data-testid="weekly-special-current-featured">
          Live now: <span className="text-white/80 font-semibold">{current.year} {current.name}</span>
          {" — "}{current.available_now ? <span className="text-emerald-400">available</span> : <span className="text-amber-400">rented{current.next_available ? ` (free ${current.next_available})` : ""}</span>}
        </p>
      )}
    </div>
  );
}

function TrackingCard({ token }) {
  const [cfg, setCfg] = useState({ meta_pixel_id: "", tiktok_pixel_id: "", countdown_enabled: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/weekly-special`).then((r) => setCfg({
      meta_pixel_id: r.data.meta_pixel_id || "",
      tiktok_pixel_id: r.data.tiktok_pixel_id || "",
      countdown_enabled: r.data.countdown_enabled !== false,
    })).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/weekly-special/config`, cfg, auth(token));
      toast.success("Weekly Special page settings saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="weekly-special-promo-card">
      <h4 className="text-white font-semibold text-sm mb-1">Ad Tracking & Countdown</h4>
      <p className="text-white/40 text-xs mb-4">
        Pixels fire a PageView on load and a Lead event on every form submission — only on the Weekly Special page.
        The QR code and share links live in the <span className="text-[#F2B705] font-semibold">Share / QR</span> tab.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="text-xs text-white/50 uppercase tracking-wider block">Meta (Facebook) Pixel ID
          <input data-testid="weekly-special-meta-pixel" value={cfg.meta_pixel_id} onChange={(e) => setCfg((p) => ({ ...p, meta_pixel_id: e.target.value.trim() }))}
            placeholder="e.g. 123456789012345"
            className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm placeholder:text-white/20" />
        </label>
        <label className="text-xs text-white/50 uppercase tracking-wider block">TikTok Pixel ID
          <input data-testid="weekly-special-tiktok-pixel" value={cfg.tiktok_pixel_id} onChange={(e) => setCfg((p) => ({ ...p, tiktok_pixel_id: e.target.value.trim() }))}
            placeholder="e.g. C1A2B3C4D5E6F7"
            className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm placeholder:text-white/20" />
        </label>
      </div>
      <div className="flex items-center justify-between pt-4">
        <label className="flex items-center gap-3 text-sm text-white/80 cursor-pointer">
          <input data-testid="weekly-special-countdown-toggle" type="checkbox" checked={cfg.countdown_enabled}
            onChange={(e) => setCfg((p) => ({ ...p, countdown_enabled: e.target.checked }))} className="w-4 h-4 accent-[#F2B705]" />
          &ldquo;Ends Sunday&rdquo; countdown timer (resets weekly)
        </label>
        <button data-testid="weekly-special-promo-save" onClick={save} disabled={saving}
          className="flex items-center gap-1.5 px-5 py-2 rounded-md bg-[#F2B705] text-black font-bold text-sm disabled:opacity-50">
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
